import type { CallLegParticipant, CallLegsResponse, UserInfo } from '../types';
import { strings } from '../strings/en';
import { canSeeInfrastructureTier } from './access';
import { isPhoneLikeLabel } from './phone';

export type JourneyTier = 'end_user' | 'technical' | 'platform';
export type JourneyQuality = 'healthy' | 'degraded' | 'poor' | 'unknown';
export type JourneyNodeKind =
  | 'carrier' | 'sbc' | 'teams' | 'main_line' | 'auto_attendant' | 'call_queue'
  | 'person' | 'you' | 'voicemail' | 'external';

export interface JourneyNode {
  id: string;
  kind: JourneyNodeKind;
  label: string;
  name: string | null;
  /**
   * Raw (unformatted) phone number for a phone-bearing node ('external' /
   * 'person'), kept separately from `label`/`name` so a caller-ID display
   * name and its number can both be shown together at render time (owner
   * morning review G3) instead of the number being silently dropped
   * whenever a name is present. `null`/omitted for every other node kind.
   */
  number?: string | null;
  quality: JourneyQuality | null;
}
export interface JourneySegment { fromId: string; toId: string; durationSeconds: number | null; outcome: string | null; }
export interface Journey { tier: JourneyTier; nodes: JourneyNode[]; segments: JourneySegment[]; finalOutcome: string | null; }

/**
 * Owner send-back F5c: this must key on the emulated view for end-user vs
 * technical (`role`/`can_see_technical_details`, both already emulation-aware
 * -- see `permissions.py`'s `can_see_technical_details` derivation), and only
 * fall through to the infrastructure tier for a real, un-emulated platform
 * admin. Checking `actual_role` alone showed Carrier/SBC/Teams to whichever
 * end user a platform admin was viewing as.
 */
export function tierForUser(
  user: Pick<UserInfo, 'role' | 'actual_role' | 'can_see_technical_details'> | null | undefined,
): JourneyTier {
  if (canSeeInfrastructureTier(user)) return 'platform';
  if (user?.can_see_technical_details) return 'technical';
  return 'end_user';
}

const normalize = (n: string | null | undefined) => (n ?? '').replace(/\D/g, '').slice(-10);

function participantKind(p: CallLegParticipant): 'auto_attendant' | 'call_queue' | 'user' | 'phone' {
  if (p.resource_type === 'auto_attendant' || p.identity_type === 'auto_attendant') return 'auto_attendant';
  if (p.resource_type === 'call_queue' || p.identity_type === 'call_queue') return 'call_queue';
  if (p.identity_type === 'user') return 'user';
  return 'phone';
}

function carrierLabel(sig: string | null | undefined): string | null {
  if (!sig) return null;
  return sig.replace(/^sg[_-]/i, '');
}

function qualityForSession(data: CallLegsResponse, sessionId: string | null): JourneyQuality | null {
  const a = data.voiceiq_assessment;
  if (!a || !sessionId) return null;
  const stream = a.microsoft.streams.find((s) => s.session_id === sessionId);
  if (stream) return stream.status;
  if (a.connected_session_id === sessionId) return a.overall_status;
  return null;
}

/**
 * A caller-ID/display name only counts as a real name when it isn't itself
 * just the number (some carriers put the digits in the display-name field)
 * -- otherwise the number would end up duplicated across `label` and
 * `number` for no benefit (owner morning review G3).
 */
function realName(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim() || null;
  return trimmed && !isPhoneLikeLabel(trimmed) ? trimmed : null;
}

export function collapseLegsForRole(
  data: CallLegsResponse,
  opts: {
    tier: JourneyTier;
    direction: string;
    ownedNumbers?: string[];
    ingressSigGroup?: string | null;
    /**
     * Fallback caller-ID fields from `CallDetail`, used only when the leg
     * data itself carries no usable identity for the counterpart -- e.g. a
     * genuine single-leg Teams-only call (no SBC leg at all), where the
     * Teams call session's caller/callee metadata for an external PSTN
     * party can be sparse or absent even though the party is known from the
     * call record itself. Without this fallback the journey renders a bare
     * "?" node instead of the caller-ID name/number (bug: single Teams leg
     * rendered as a lone "?" node / "? called.").
     */
    detail?: {
      callingNumber?: string | null;
      callingName?: string | null;
      calledNumber?: string | null;
      /**
       * Call-level outcome/duration, used only to synthesize a terminal node
       * when there is genuinely no leg data at all (`data.legs.length === 0`)
       * -- observed for real direct SBC-only calls with no Teams
       * correlation (bug: journey stopped at the single initial node,
       * "You called You." / missing the "you answered" clause).
       */
      outcome?: string | null;
      durationSeconds?: number | null;
    };
  },
): Journey {
  const legs = [...data.legs].sort((a, b) => a.leg_order - b.leg_order);
  const owned = new Set((opts.ownedNumbers ?? []).map(normalize));
  const nodes: JourneyNode[] = [];
  const segments: JourneySegment[] = [];
  const push = (n: JourneyNode) => { if (!nodes.some((x) => x.id === n.id)) nodes.push(n); return n.id; };
  const link = (fromId: string, toId: string, duration: number | null, outcome: string | null) => {
    const last = segments[segments.length - 1];
    if (last && last.toId === toId && last.fromId === fromId) {
      last.durationSeconds = (last.durationSeconds ?? 0) + (duration ?? 0);
      return;
    }
    segments.push({ fromId, toId, durationSeconds: duration, outcome });
  };

  const sbcLeg = legs.find((l) => l.source !== 'teams') ?? legs[0];
  const outbound = opts.direction === 'outbound';
  const external = outbound ? sbcLeg?.callee : sbcLeg?.caller;
  const externalName = realName(external?.display_name) ?? (outbound ? null : realName(opts.detail?.callingName));
  const externalNumber = external?.phone_number
    ?? (outbound ? opts.detail?.calledNumber : opts.detail?.callingNumber)
    ?? null;
  const externalLabel = externalName || externalNumber || '?';
  // Outbound reads left-to-right from the person who dialled; inbound from whoever called in.
  let cursor = outbound
    ? push({ id: 'you', kind: 'you', label: strings.interaction.you, name: null, quality: null })
    : push({ id: 'external', kind: 'external', label: externalLabel, name: externalName, number: externalNumber, quality: null });

  if (opts.tier === 'platform') {
    const c = carrierLabel(opts.ingressSigGroup);
    cursor = (() => { const id = push({ id: 'carrier', kind: 'carrier', label: strings.interaction.carrier(c), name: null, quality: null }); link(cursor, id, null, null); return id; })();
    const sbcQ = data.voiceiq_assessment ? data.voiceiq_assessment.ribbon.status : null;
    cursor = (() => { const id = push({ id: 'sbc', kind: 'sbc', label: strings.interaction.sbc, name: null, quality: sbcQ }); link(cursor, id, null, null); return id; })();
    if (data.has_teams_sessions) {
      cursor = (() => { const id = push({ id: 'teams', kind: 'teams', label: strings.interaction.teams, name: null, quality: null }); link(cursor, id, null, null); return id; })();
    }
  }

  const teamsLegs = legs.filter((l) => l.source === 'teams');
  const routingLegs = teamsLegs.filter((l) => ['auto_attendant', 'call_queue'].includes(participantKind(l.callee)));
  // Every Teams leg that isn't a routing hop (AA/CQ) is a "final" leg -- this
  // includes a callee typed 'user' (an internal Teams user) *and* a callee
  // typed 'phone' (an external PSTN party reached by a Teams-only call with
  // no accompanying SBC leg, e.g. an outbound call placed from the Teams
  // client). Restricting this to 'user' only (the pre-fix bug) silently
  // dropped every genuine single-Teams-leg call from `finalLegs`, leaving
  // the journey with nothing after the initial node -- "You called You."
  const finalTeamsLegs = teamsLegs.filter((l) => !['auto_attendant', 'call_queue'].includes(participantKind(l.callee)));

  // Outbound calls never pass through our own main line / attendant / queue.
  // End users get the same attendant/queue breakdown as the technical tier
  // (spec §6 send-back F5d: "reached the attendant, waited in the queue
  // 0:41" needs the individual hops, not one aggregated main-line wait) --
  // the only thing gated by tier is the Carrier/SBC/Teams infrastructure
  // block above, not the product-level AA/CQ steps.
  if (!outbound && routingLegs.length > 0) {
    const ml = push({ id: 'main_line', kind: 'main_line', label: strings.interaction.mainLine, name: null, quality: null });
    link(cursor, ml, null, null);
    cursor = ml;
    routingLegs.forEach((l, i) => {
      const kind = participantKind(l.callee) as 'auto_attendant' | 'call_queue';
      // An end user still sees every hop and its timing, but not the
      // resource's configured display name (e.g. "AA-Reception") -- that
      // stays technical/platform-tier detail, same as before this fix
      // unified the two branches (spec §6; InteractionDetail.test.tsx
      // "end user does not see attendant names").
      const name = opts.tier === 'end_user' ? null : (l.callee.display_name ?? null);
      const id = push({
        id: `${kind}-${i}`,
        kind,
        label: kind === 'auto_attendant' ? strings.interaction.autoAttendant(name) : strings.interaction.callQueue(name),
        name,
        quality: 'unknown',
      });
      link(cursor, id, l.duration_seconds, l.outcome);
      cursor = id;
    });
  }

  const finalLegs = finalTeamsLegs.length > 0 ? finalTeamsLegs : legs.filter((l) => l.source !== 'teams');

  // A genuinely leg-less call (no SBC leg synced, no Teams correlation --
  // seen for real direct/no-answer calls) leaves `finalLegs` empty with
  // nothing after the initial node. Synthesize the terminal node from the
  // call-level outcome/duration rather than leaving the journey at one node
  // (bug: "? called." / "You called You." with no second clause).
  if (legs.length === 0) {
    const outcome = opts.detail?.outcome ?? null;
    const duration = opts.detail?.durationSeconds ?? null;
    const id = outbound
      ? push({ id: 'external', kind: 'external', label: externalLabel, name: externalName, number: externalNumber, quality: null })
      : push({ id: 'you', kind: 'you', label: strings.interaction.you, name: null, quality: null });
    link(cursor, id, duration, outcome);
    cursor = id;
    return { tier: opts.tier, nodes, segments, finalOutcome: outcome };
  }

  finalLegs.forEach((l, i) => {
    const target = l.callee;
    if (outbound) {
      // The terminal node is the party we dialled, whatever the leg source says.
      const id = push({ id: 'external', kind: 'external', label: externalLabel, name: externalName, number: externalNumber, quality: qualityForSession(data, l.session_id) });
      link(cursor, id, l.duration_seconds, l.outcome);
      cursor = id;
      return;
    }
    const isYou = owned.has(normalize(target.phone_number));
    const isVoicemail = l.outcome === 'voicemail';
    const targetName = realName(target.display_name);
    const targetNumber = target.phone_number ?? null;
    const id = push({
      id: isVoicemail ? `voicemail-${i}` : isYou ? 'you' : `person-${i}`,
      kind: isVoicemail ? 'voicemail' : isYou ? 'you' : 'person',
      label: isVoicemail ? strings.interaction.voicemail : isYou ? strings.interaction.you : (targetName || targetNumber || '?'),
      name: isVoicemail || isYou ? null : targetName,
      number: isVoicemail || isYou ? null : targetNumber,
      quality: qualityForSession(data, l.session_id),
    });
    link(cursor, id, l.duration_seconds, l.outcome);
    cursor = id;
  });

  const finalOutcome = finalLegs.length ? finalLegs[finalLegs.length - 1].outcome : (sbcLeg?.outcome ?? null);
  return { tier: opts.tier, nodes, segments, finalOutcome };
}
